import React, { useState, useRef, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import PanoView from "../panoView";
import Welcome from "../welcome";
import MiniMap from "../miniMap";
import ActionBar from "../components/actionBar";
import AudioPlayer from "../components/audioPlayer";
import BackgroundSound from "../components/backgroundSound";
import { getStaticTourById, getStaticTours } from "../../data/staticTours";
import { PanoContext } from '../../contexts/PanoContextContainer';
import Sidebar from '../components/sidebar';
import FilterResults from '../filterResults';
import "./style.css";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function resolveStartSceneIndex(config, startSceneImage) {
  if (!Array.isArray(config) || config.length === 0 || !startSceneImage) {
    return 0;
  }

  const normalizedTarget = startSceneImage.trim().toLowerCase();
  const matchedIndex = config.findIndex((scene) => {
    const imagePath = scene?.image || '';
    const fileName = imagePath.split('/').pop() || '';
    return fileName.toLowerCase() === normalizedTarget;
  });

  return matchedIndex >= 0 ? matchedIndex : 0;
}

function Home() {
  const childRef = useRef();
  const [currentScene, setCurrentScene] = useState(0);
  const [isSideBarOpen, setIsSideBarOpen] = useState(false);
  const [isFilterResults, setIsFilterResults] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({});
  const actionBarRef = useRef();

  const { showLoader, setShowLoader, setSelected: setInitialScene } = useContext(PanoContext);

  const [tourData, setTourData] = useState(null);
  const query = useQuery();

  useEffect(() => {
    window.scrollTo(0, 0);

    function loadTour() {
      setShowLoader(true);
      try {
        let tourId = query.get('tour');

        // If no tour specified, load the static manifest and pick the first one
        if (!tourId) {
          const tours = getStaticTours();
          if (tours && tours.length > 0) {
            tourId = tours[0].id;
            // Optionally update URL without reloading
            window.history.replaceState(null, '', `?tour=${tourId}`);
          }
        }

        if (tourId) {
          const data = getStaticTourById(tourId);
          if (data) {
            const startSceneIndex = resolveStartSceneIndex(data.config, data.metadata?.startSceneImage);
            setCurrentScene(startSceneIndex);
            setInitialScene(startSceneIndex);
            setTourData(data); // { metadata, config }
          } else {
            console.error('Failed to load static tour data');
          }
        }
      } catch (err) {
        console.error('Error loading static tour:', err);
      }
    }

    loadTour();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  const changeScene = (i) => {
    if (childRef.current) {
      childRef.current.changeScene(i);
    } else {
      setCurrentScene(i);
    }
    setIsSideBarOpen(false);
    setIsFilterResults(false);
    if (actionBarRef.current) actionBarRef.current.closeFilter();
  }

  const openInfoSpot = (i, j) => {
    if (childRef.current) {
      childRef.current.changeScene(j);
      childRef.current.openInfoSpot(i, j);
    } else {
      setCurrentScene(j);
    }
    setIsSideBarOpen(false);
    setIsFilterResults(false);
    if (actionBarRef.current) actionBarRef.current.closeFilter();
  }

  const openProductModal = (i, j) => {
    if (childRef.current) {
      childRef.current.changeScene(j);
      childRef.current.openProductModal(i, j);
    } else {
      setCurrentScene(j);
    }
    setIsSideBarOpen(false);
    if (actionBarRef.current) actionBarRef.current.closeFilter();
  }

  const openFilterResults = (selectedItems) => {
    setIsFilterResults(true);
    setSelectedFilters(selectedItems);
    if (actionBarRef.current) actionBarRef.current.closeFilter();
  }

  if (!tourData || !tourData.config || tourData.config.length === 0) {
    return <div style={{ color: 'white', textAlign: 'center', marginTop: '20vh' }}>Loading tour...</div>;
  }

  // Build audio paths from the scene's sceneAudio field (set in admin editor)
  const currentSceneData = tourData.config[currentScene];
  
  const voFolderMale = tourData.metadata.mainVoFolderMale || '';
  const voFolderFemale = tourData.metadata.mainVoFolderFemale || '';
  
  const audioFileMale = currentSceneData?.sceneAudio;
  const audioFileFemale = currentSceneData?.sceneAudioFemale;
  
  // Helper to strip 'public/' prefix
  const cleanPublic = (folder) => {
    if (!folder) return '';
    if (folder.startsWith('public/')) return folder.substring(7);
    if (folder.startsWith('public')) return folder.substring(6);
    return folder;
  };

  // Determine audio source (prefer male audio)
  let audioSrc = null;
  if (audioFileMale && voFolderMale) {
    audioSrc = `/${cleanPublic(voFolderMale)}/${audioFileMale}.mp3`.replace('//', '/');
  } else if (audioFileFemale && voFolderFemale) {
    audioSrc = `/${cleanPublic(voFolderFemale)}/${audioFileFemale}.mp3`.replace('//', '/');
  } else if (tourData.metadata.mainVoFolder && currentSceneData?.sceneAudio) {
    audioSrc = `/${cleanPublic(tourData.metadata.mainVoFolder)}/${currentSceneData.sceneAudio}.mp3`.replace('//', '/');
  }

  // Determine SRT source (prefer female folder since SRT files are typically there)
  let srtSrc = null;
  const srtFile = audioFileFemale || audioFileMale || currentSceneData?.sceneAudio;
  if (srtFile && voFolderFemale) {
    srtSrc = `/${cleanPublic(voFolderFemale)}/${srtFile}.srt`.replace('//', '/');
  } else if (srtFile && voFolderMale) {
    srtSrc = `/${cleanPublic(voFolderMale)}/${srtFile}.srt`.replace('//', '/');
  } else if (srtFile && tourData.metadata.mainVoFolder) {
    srtSrc = `/${cleanPublic(tourData.metadata.mainVoFolder)}/${srtFile}.srt`.replace('//', '/');
  }

  const hasSceneAudio = !!audioSrc;
  const backgroundAudioFile = tourData.metadata?.backgroundAudioFile || '';
  const backgroundAudioVolume = typeof tourData.metadata?.backgroundAudioVolume === 'number'
    ? tourData.metadata.backgroundAudioVolume
    : 0.1;
  const backgroundAudioSrc = backgroundAudioFile
    ? `/${cleanPublic(tourData.metadata?.baseFolder || '')}/${backgroundAudioFile}`.replace('//', '/')
    : null;

  return (
    <div>
      <Sidebar isOpen={isSideBarOpen} changeScene={changeScene} openInfoSpot={openInfoSpot} tourConfig={tourData.config} />
      <MiniMap changeScene={changeScene} currentScene={currentScene} openInfoSpot={openInfoSpot} />
      <Welcome changeScene={changeScene} openInfoSpot={openInfoSpot} />

      <PanoView
        ref={childRef}
        setCurrent={setCurrentScene}
        initialSceneIndex={currentScene}
        tourConfig={tourData.config}
        tourMeta={tourData.metadata}
      />

      {isFilterResults && <FilterResults openProductModal={openProductModal} openInfoSpot={openInfoSpot} selected={selectedFilters} closeFilterResults={() => setIsFilterResults(false)} tourConfig={tourData.config} />}
      <ActionBar ref={actionBarRef} openFilterResults={openFilterResults} />
      <BackgroundSound
        audioSrc={backgroundAudioSrc}
        volume={backgroundAudioVolume}
        isPlaying={!showLoader && !!backgroundAudioSrc}
      />

      {!showLoader && hasSceneAudio && (
        <AudioPlayer
          key={`audio-scene-${currentScene}`}
          audioSrc={audioSrc}
          srtSrc={srtSrc}
          autoPlay={true}
          showControls={true}
        />
      )}
      <div className={`home-fade-from-black ${!showLoader ? 'home-fade-out' : ''}`}></div>
    </div>
  );
}

export default Home;
